import { type ChartOptions } from "chart.js";

import { type GeneralChartSelectorType } from "@features/mmm/hooks/mmm-url-state";
import { type User } from "@internal/user";
import { MetricFormatters } from "@north-beam/nb-client-side";

import { KpiType } from "../../explore-budgets/client-setting/types";

import { chartTooltip } from "./chart-tooltip";
import { REVENUE_ID } from "./const";

export const chartOptions = (
  user: User,
  kpiType: KpiType,
  selectValue: GeneralChartSelectorType,
): ChartOptions<"line"> => {
  const isRevenue = kpiType === KpiType.REVENUE;

  return {
    responsive: true,
    maintainAspectRatio: true,
    interaction: {
      mode: "index" as const,
      intersect: false,
      position: "nearest",
    },
    plugins: {
      legend: { display: false },
      zoom: {
        zoom: {
          wheel: {
            modifierKey: "alt",
            enabled: true,
          },
          drag: {
            modifierKey: "alt",
            enabled: true,
          },
          mode: "x",
        },
      },
      tooltip: {
        enabled: false,
        external: chartTooltip,
        user,
        isRevenue,
        selectValue,
      },
      annotation: {
        clip: false,
        common: { drawTime: "beforeDraw" },
      },
    },
    scales: {
      [REVENUE_ID]: {
        type: "linear" as const,
        display: true,
        suggestedMax: 5,
        axis: "y",
        position: "left" as const,
        grace: "50%",
        beginAtZero: true,
        title: {
          display: true,
          padding: 0,
        },
        grid: {
          drawTicks: false,
          drawBorder: false,
          drawOnChartArea: true,
        },
        ticks: {
          maxTicksLimit: 6,
          padding: 12,
          font: {
            family: "Commons Pro Mono",
            size: 11,
          },
          callback: (val) =>
            selectValue === "spend"
              ? MetricFormatters.currency(
                  Number(val),
                  user.currencies.currentCurrency,
                  {
                    precision: "approximate",
                  },
                )
              : selectValue === "roas" && isRevenue
              ? MetricFormatters.multiplier(
                  Number(val),
                  user.currencies.currentCurrency,
                  {
                    precision: "approximate",
                  },
                )
              : selectValue === "roas" && !isRevenue
              ? MetricFormatters.currency(
                  Number(val),
                  user.currencies.currentCurrency,
                  {
                    precision: "approximate",
                  },
                )
              : isRevenue
              ? MetricFormatters.currency(
                  Number(val),
                  user.currencies.currentCurrency,
                  {
                    precision: "approximate",
                  },
                )
              : MetricFormatters.decimal(
                  Number(val),
                  user.currencies.currentCurrency,
                  {
                    precision: "approximate",
                  },
                ),
        },
      },

      x: {
        axis: "x",
        grid: {
          drawOnChartArea: false,
          drawBorder: false,
          drawTicks: false,
        },
        ticks: {
          padding: 20,
          maxRotation: 0,
          autoSkip: true,
          font: {
            family: "Commons Pro",
            size: 13,
          },
        },
      },
    },
  } as ChartOptions<"line">;
};
